import { useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Table,
  Typography,
  Button,
  Empty,
  Alert,
  Skeleton,
  Space,
  Tag,
  Progress,
  Tooltip,
} from 'antd';
import { useGetStylingWorkflowsInfiniteQuery } from '../../store/stylist/stylistApi';
import { useFetchOnListEnd } from '@admin-panel/hooks';
import { removeUnderscoresAndCapitalize } from '@admin-panel/utils';
import { StylingWorkflowListItemDTOType } from '../../schemas';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  clearCurrentWorkflow,
  selectStylingWorkflowFilters,
  setIsWorkflowDrawerOpen,
  setCurrentWorkflowId,
  setCurrentWorkflow,
  setIsStylingWorkflowDrawerOpen,
} from '../../store/stylist/slice';
import Discovery from './Discovery';
import CreateStylingWorkflowDrawer from './CreateStylingWorkflowDrawer';
import StylingWorkflowDetailDrawer from './StylingWorkflowDetailDrawer';
import * as Styles from './styles';
import ReRunFailedTasks from './ReRunFailedTasks';

const { Title } = Typography;

type StylingJobsProps = {
  workflowGenre?: 'standard' | 'occasion';
  title?: string;
};

const StylingJobs = ({ workflowGenre = 'standard', title = 'Styling Jobs' }: StylingJobsProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const filters = useAppSelector(selectStylingWorkflowFilters);

  const {
    data: workflowsData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useGetStylingWorkflowsInfiniteQuery({
    filters,
    workflow_genre: workflowGenre,
  });

  const workflows = useMemo(() => {
    if (!workflowsData?.pages) {
      return [];
    }
    const all = workflowsData.pages.flatMap(page => page?.results || []);
    return all.filter(workflow => workflow.workflow_genre === workflowGenre);
  }, [workflowsData, workflowGenre]);

  const [selectedWorkflow, setSelectedWorkflow] = useState<StylingWorkflowListItemDTOType | null>(
    null,
  );

  const openWorkflowDetail = useCallback(
    (workflow: StylingWorkflowListItemDTOType) => {
      dispatch(setCurrentWorkflowId(workflow.id));
      dispatch(setCurrentWorkflow(workflow));
      dispatch(setIsWorkflowDrawerOpen(true));

      setSelectedWorkflow(workflow);

      // Update URL
      // const params = new URLSearchParams(location.search);
      // params.set('workflowDetail', workflow.id.toString());
      // navigate(`${location.pathname}?${params.toString()}`, { replace: false });
    },
    [dispatch],
  );

  const closeWorkflowDetail = useCallback(() => {
    setSelectedWorkflow(null);
    dispatch(clearCurrentWorkflow());
    dispatch(setIsWorkflowDrawerOpen(false));

    // Remove from URL
    // const params = new URLSearchParams(location.search);
    // params.delete('workflowDetail');
    // navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`, {
    //   replace: false,
    // });
  }, [dispatch]);

  const handleCreateWorkflow = () => {
    dispatch(setIsStylingWorkflowDrawerOpen(true));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'completed':
        return 'blue';
      case 'failed':
        return 'red';
      case 'paused':
        return 'orange';
      default:
        return 'default';
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name: string, record: StylingWorkflowListItemDTOType) => (
          <Button
            type="link"
            onClick={() => openWorkflowDetail(record)}
            style={{ padding: 0, textAlign: 'left' }}
          >
            <Typography.Text strong>{name}</Typography.Text>
          </Button>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: string, record: StylingWorkflowListItemDTOType) => (
          <>
            <Tag color={getStatusColor(status)}>{removeUnderscoresAndCapitalize(status)}</Tag>
            {status === 'failed' && record.error_message && (
              <Tooltip title={record.error_message}>
                <Typography.Text
                  type="danger"
                  style={{
                    marginLeft: 8,
                    maxWidth: 240,
                    display: 'inline-block',
                    cursor: 'help',
                  }}
                  ellipsis
                >
                  {record.error_message}
                </Typography.Text>
              </Tooltip>
            )}
          </>
        ),
      },
      {
        title: 'Progress',
        key: 'progress',
        render: (record: StylingWorkflowListItemDTOType) => (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Progress
              percent={record.progress_percentage}
              size="small"
              status={record.status === 'failed' ? 'exception' : 'normal'}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {record.completed_items} / {record.total_items} items
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: 'Created',
        dataIndex: 'created_on',
        key: 'created_on',
        render: (date: string) => new Date(date).toLocaleDateString(),
      },
      {
        title: 'Re-Run',
        dataIndex: 'status',
        key: 'status',
        render: (data: string, record: StylingWorkflowListItemDTOType) => {
          if (data !== 'failed') {
            return;
          }

          return <ReRunFailedTasks workflow={record} />;
        },
      },
    ],
    [openWorkflowDetail],
  );

  const { lastItemRef } = useFetchOnListEnd(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  if (error) {
    return (
      <Styles.Wrapper>
        <Alert
          message="Error loading styling workflows"
          description="Please try again later."
          type="error"
          showIcon
        />
      </Styles.Wrapper>
    );
  }

  return (
    <Styles.Wrapper>
      <Styles.Header>
        <Title level={2} style={{ margin: 0 }}>
          {title}
        </Title>
        <Button type="primary" onClick={handleCreateWorkflow}>
          Create Styling Workflow
        </Button>
      </Styles.Header>

      <Discovery />

      <Styles.Content>
        {isLoading && workflows.length === 0 ? (
          <Skeleton active />
        ) : workflows.length === 0 ? (
          <Empty description="No styling workflows found" />
        ) : (
          <Table
            dataSource={workflows}
            columns={columns}
            rowKey="id"
            pagination={false}
            loading={isFetchingNextPage}
            scroll={{ x: 800 }}
          />
        )}

        {workflows.length > 0 && <div ref={lastItemRef} style={{ height: 20 }} />}
      </Styles.Content>

      <StylingWorkflowDetailDrawer workflow={selectedWorkflow} onClose={closeWorkflowDetail} />

      <CreateStylingWorkflowDrawer workflowGenre={workflowGenre} />
    </Styles.Wrapper>
  );
};

export default StylingJobs;
